/* eslint-disable no-nested-ternary */
import { Divider, Grid, Typography } from '@mui/material';
import { useLazyQuery, useMutation } from '@apollo/client';
import { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import moment from 'moment';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import './userpanel.scss';
import { AvatarImage } from '../Avatar';
import { CAN_ADD_TIMEBLOCK } from '../../constants/permissions';
import { DELETE_TIME_BLOCK, GET_USERS_PROJECT_SKILL, GET_USERS_TIMEBLOCK } from '../../apis/user';
import { useAuth } from '../../context/AuthContext';
import Button from '../atoms/button';
import DateRangePicker from './DateRangePicker';
import deleteImg from '../../images/deleteIcon.svg';
import LoadingOverlay from '../LoadingOverlay';
import Transition from '../MemberList/Transition';
import useProjectVersionHistory from '../../context/VersionHistoryContext';
import UserProjects from './UserProjects';
import useWorkspace from '../../context/WorkspaceContext';

const propTypes = {
  user: PropTypes.oneOfType([PropTypes.object]),
  sprintData: PropTypes.oneOfType([PropTypes.object]),
};

const defaultProps = {
  user: {},
  sprintData: {},
};

const CustomDialogContent = styled(DialogContent)`
  &.MuiDialogContent-root {
    color: #101828;
    font-weight: 600;
    font-size: 16px;
    background: #f9fafb;
    display: flex;
    flex-direction: row;
    align-items: center;
    padding: 16px 20px;
    gap: 4px;
  }
`;

const readableDates = (startDate, endDate) => {
  const momentStartDate = moment(startDate);
  const momentEndDate = moment(endDate);
  const diffStartDate = momentStartDate.diff(moment().startOf('day'), 'days');
  const diffEndDate = momentEndDate.diff(moment().startOf('day'), 'days');

  if (diffStartDate === diffEndDate) {
    return diffEndDate >= 0 ? (diffEndDate === 0 ? 'Today' : (diffEndDate === 1 ? 'Tomorrow' : momentEndDate.format('MMM DD, YYYY'))) : momentEndDate.format('MMM DD, YYYY');
  }
  const startDateString = diffStartDate >= 0 ? (diffStartDate === 0 ? 'Today' : (diffStartDate === 1 ? 'Tomorrow' : momentStartDate.format('MMM DD, YYYY'))) : momentStartDate.format('MMM DD, YYYY');
  const endDateString = diffEndDate >= 0 ? (diffEndDate === 0 ? 'Today' : (diffEndDate === 1 ? 'Tomorrow' : momentEndDate.format('MMM DD, YYYY'))) : (diffEndDate === -1 ? 'Yesterday' : momentEndDate.format('MMM DD, YYYY'));
  return `${startDateString} - ${endDateString}`;
};

const UserPanel = (props) => {
  let userKey;
  let userId;
  let userFullName;
  let userEmail;
  let profileId;
  let userLogo;

  const { user, sprintData } = props;

  if (Object.keys(sprintData).length !== 0 && sprintData !== undefined && sprintData !== null) {
    const { sprint } = sprintData;
    userKey = user[`${sprint}_googleId`];
    userId = parseInt(user[`${sprint}_userId`], 10);
    profileId = parseInt(user[`${sprint}_profileId`], 10);
    userFullName = user[`${sprint}_label`];
    userEmail = user[`${sprint}_email`];
    userLogo = user[`${sprint}_logo`];
  } else {
    profileId = parseInt(user.data.id, 10);
    userKey = user.data.googleId;
    userId = parseInt(user.data.uId, 10);
    userFullName = user.data.displayName ?? user.value;
    userEmail = user.data.emailAddress;
    userLogo = user.data.logo;
  }

  const [open, setOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [userProjectAllocationData, setUserProjectAllocationData] = useState([]);
  const [rowData, setRowData] = useState([]);
  const { userPermissions } = useWorkspace();
  const { authUser } = useAuth();
  const { selectedListItem, showVersionHistoryBar, readOnlyMode } = useProjectVersionHistory();

  const [isLoading, setIsLoading] = useState(true);

  const currentUser = authUser.id;
  const permissions = [];
  if (userPermissions !== undefined) {
    if (userPermissions.permissions !== undefined) {
      userPermissions.permissions.map((x) => permissions.push(x));
    }
  }

  const [getUsersTimeBlock] = useLazyQuery(GET_USERS_TIMEBLOCK, { fetchPolicy: 'no-cache', variables: { userId } });

  const [getUserProjectAllocationData] = useLazyQuery(GET_USERS_PROJECT_SKILL, { fetchPolicy: 'no-cache', variables: { userId, profileId } });

  const userProjectSkillDetails = async () => {
    const { data } = await getUserProjectAllocationData();
    setUserProjectAllocationData(data?.getUserProjectAllocationData);
  };

  const [deleteTimeBlock] = useMutation(DELETE_TIME_BLOCK);

  const isHidden =
    showVersionHistoryBar &&
    readOnlyMode &&
    selectedListItem !== null &&
    selectedListItem !== undefined;

  const getTimeBlock = async () => {
    const { data } = await getUsersTimeBlock();
    if (data?.getUsersTimeBlock?.length >= 0) {
      const usersTimeBlockData = data.getUsersTimeBlock.map((row) => ({
        tId: row.id,
        uId: row.user_id,
        start_date: row.start_date,
        end_date: row.end_date,
      }));
      setRowData(usersTimeBlockData);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!isLoading) setIsLoading(true);
      await Promise.all([
        getTimeBlock(),
        userProjectSkillDetails(),
      ]);
      setIsLoading(false);
    };
    if (open) fetchData();
  }, [open]);

  const timeBlockDestroy = async () => {
    setIsLoading(true);
    setDeleteId(null);
    const variables = {
      input: {
        id: parseInt(deleteId, 10),
      },
    };
    const deletedTimeBlock = await deleteTimeBlock({ variables });
    if (deletedTimeBlock.data.deleteTimeBlock) await getTimeBlock();
    setIsLoading(false);
  };

  const canAddTimeBlock =
    permissions.includes(CAN_ADD_TIMEBLOCK) || parseInt(currentUser, 10) === parseInt(userId, 10);

  return (
    <div className="user-name-main-btn">
      <button type="button" style={{ color: isHidden ? '#101828 !important' : '' }} disabled={isHidden} onClick={() => setOpen(true)}>
        {userFullName}
      </button>
      <Dialog
        className="custom-modal-dialog user-details-modal-dialog"
        fullWidth
        TransitionComponent={Transition}
        open={open}
        onClose={() => setOpen(false)}
        aria-labelledby="responsive-dialog-title"
      >
        <LoadingOverlay open={isLoading} />
        <DialogTitle id="#responsive-dialog-title">
          <CloseOutlinedIcon
            className="close-icon"
            style={{ cursor: 'pointer' }}
            onClick={() => setOpen(false)}
          />
          <Box className="viewUser">
            <Box className="viewUserImage">
              <AvatarImage placeHolder="" borderGray key={userKey} alt="Profile Logo" src={userLogo} initials={2} fontWeight={600} fontSize={35} height={70} width={70} name={userFullName} />
              <Box>
                <Typography className="user-name-bold" variant="h4">
                  {userFullName}
                </Typography>
                <Typography className="email-id">{userEmail}</Typography>
              </Box>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent className="dialog-content">
          <Box>
            <DateRangePicker
              setIsLoading={setIsLoading}
              userId={userId}
              userName={userFullName}
              onSave={getTimeBlock}
              canAddTimeBlock={canAddTimeBlock}
            />
          </Box>
          {rowData.length > 0 ? (
            rowData.map((item) => {
              const displayDate = readableDates(item.start_date, item.end_date);
              return (
                <div key={item.tId}>
                  <div className="time-block-detail">
                    <span className="display-date">{displayDate}</span>
                    <div className="status-btn">
                      <span className="status"> Unavailable</span>
                      {canAddTimeBlock && (
                        <Button onClick={() => setDeleteId(item.tId)}>
                          <img src={deleteImg} alt="deleteButton" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="user-panel">
              <Box className="text-center">No Time Block found</Box>
            </div>
          )}
          <Divider sx={{ my: 2 }} />
          <div className="user-detail-mt">
            <Box>
              <UserProjects
                userProjectAllocationData={userProjectAllocationData}
                userId={userId}
                userProjectSkillDetails={userProjectSkillDetails}
              />
            </Box>
          </div>
        </DialogContent>

        <Dialog
          className="custom-modal-dialog"
          fullWidth
          open={!!deleteId}
          onClose={() => setDeleteId(null)}
        >
          <CustomDialogContent ml={2}>
            <Grid>
              <Grid item mt={1}>
                <DialogTitle style={{ fontSize: '1.50rem' }}>Delete Time Block</DialogTitle>
                <DialogContentText ml={3}>
                  Are you sure you want to delete the time block?
                </DialogContentText>
              </Grid>
            </Grid>
          </CustomDialogContent>
          <DialogActions sx={{ padding: '20px 25px 25px 10px' }}>
            <Button primary outlined small onClick={() => setDeleteId(null)}>Cancel</Button>
            <Button primary small onClick={() => timeBlockDestroy()}>Confirm Delete</Button>
          </DialogActions>
        </Dialog>
      </Dialog>
    </div>
  );
};
UserPanel.propTypes = propTypes;
UserPanel.defaultProps = defaultProps;
export default UserPanel;
